import React, { useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message } from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import GenericTable from "../../../../Components/Tables/GenericTable";
import GenericModal from "../../../../Components/Modals/GenericModal";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import usePageAccess from "../../../../Utlis/usePageAccess";
import {
  getAdminVehicleTypes,
  createVehicleType,
  updateVehicleType,
  deleteVehicleType,
} from "../../../../API/Admin/SystemConfiguration/VehicleTypes";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const mapVehicleType = (item, index) => ({
  key: String(item.id ?? index),
  id: item.id,
  name: item.name || "-",
  rawStatus: item.status,
  status: capitalize(item.status) || "-",
});

const VehicleTypes = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("system-configuration");
  const [dataSource, setDataSource] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchVehicleTypes = async () => {
    try {
      const res = await getAdminVehicleTypes();
      const list = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
        setDataSource(list.map(mapVehicleType));
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load vehicle types."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load vehicle types."),
      );
    }
  };

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  const openAdd = () => {
    setEditRecord(null);
    setIsFormOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setIsFormOpen(true);
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Add Vehicle Type",
        icon: <PlusCircleOutlined />,
        onClick: openAdd,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs]);

  const handleSave = async (values) => {
    const payload = {
      name: (values.name || "").trim(),
      status: values.status,
    };

    try {
      const res = editRecord
        ? await updateVehicleType(editRecord.id, payload)
        : await createVehicleType(payload);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(
            res?.data,
            editRecord
              ? "Vehicle type updated successfully"
              : "Vehicle type added successfully",
          ),
        );
        setIsFormOpen(false);
        setEditRecord(null);
        fetchVehicleTypes();
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to save vehicle type."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to save vehicle type."),
      );
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteVehicleType(deleteTarget?.id);

      if (Math.floor(res?.status / 100) === 2) {
        message.success("Vehicle type deleted successfully");
        setDataSource((prev) =>
          prev.filter((item) => item.key !== deleteTarget?.key),
        );
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to delete vehicle type."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete vehicle type."),
      );
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const statusFilters = [...new Set(dataSource.map((item) => item.status))]
    .filter(Boolean)
    .map((val) => ({ text: val, value: val }));

  const columns = [
    {
      title: "Vehicle Type",
      dataIndex: "name",
      key: "name",
      enableSearch: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${String(status).toLowerCase()}`}>
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            label: "Edit",
            style: { color: "#4F46E5", fontWeight: 500 },
            icon: <EditOutlined />,
            onClick: () => openEdit(record),
          },
          {
            key: "delete",
            label: "Delete",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => {
              setDeleteTarget(record);
              setIsDeleteOpen(true);
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                width: 24,
                height: 24,
              }}
            >
              <BsThreeDotsVertical style={{ fontSize: 20, color: "#4F46E5" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="vehicle-types">
      <GenericTable
        dataSource={dataSource}
        columns={columns.filter((c) => canEdit || c.key !== "actions")}
      />

      <GenericModalForm
        open={isFormOpen}
        title={editRecord ? "Edit Vehicle Type" : "Add Vehicle Type"}
        subtitle={
          editRecord
            ? "Update the vehicle type details"
            : "Create a new vehicle type"
        }
        okText={editRecord ? "Save Changes" : "Add Vehicle Type"}
        cancelText="Cancel"
        fields={[
          {
            name: "name",
            label: "Vehicle Type",
            placeholder: "Enter vehicle type",
            type: "input",
            rules: [
              { required: true, message: "Vehicle type is required" },
              { whitespace: true, message: "Vehicle type is required" },
            ],
          },
          {
            name: "status",
            label: "Status",
            placeholder: "Select status",
            type: "select",
            options: STATUS_OPTIONS,
            rules: [{ required: true, message: "Status is required" }],
          },
        ]}
        initialValues={{
          name: editRecord?.name,
          status: editRecord?.rawStatus || "active",
        }}
        onCancel={() => {
          setIsFormOpen(false);
          setEditRecord(null);
        }}
        onOk={handleSave}
      />

      <GenericModal
        open={isDeleteOpen}
        title="Delete Vehicle Type"
        subtitle={`Are you sure you want to delete ${deleteTarget?.name || "this vehicle type"}?`}
        highlightText={deleteTarget?.name || ""}
        showReason={false}
        confirmText="Delete"
        cancelText="Cancel"
        iconColor="#DC2626"
        iconBgColor="#FEE2E2"
        confirmBtnColor="#DC2626"
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default VehicleTypes;
